import { useCallback, useEffect, useState } from 'react';
import {
  CASH_MOVEMENT_CATEGORIES,
  SUBSCRIPTION_CATEGORIES,
  type AgingBucket,
  type AgingResponse,
  type CashForecastBucket,
  type CashMovement,
  type CashMovementCategory,
  type CashMovementType,
  type DsoResponse,
  type PublicUser,
  type Subscription,
  type SubscriptionBillingCycle,
  type SubscriptionCategory,
  type SubscriptionStatus,
  type SubscriptionsSummary,
  type TopDebtor,
  type TreasuryKpis,
} from '@otter/shared';
import { api } from '../lib/api';
import { fmt, fmtDate, todayIso } from '../lib/format';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { DateInput } from '../components/DateInput';
import { EditIcon, TrashIcon } from '../components/Icons';

type Props = {
  user: PublicUser;
  scope: string;
  onToast: (msg: string, kind?: 'ok' | 'err') => void;
  onError: (e: unknown) => void;
};

type CashMovementsResponse = {
  items: CashMovement[];
  totals: { income: number; expense: number; delta: number };
};

type SubscriptionsResponse = {
  items: Subscription[];
  summary: SubscriptionsSummary;
};

const SUBSCRIPTION_CATEGORY_LABELS: Record<SubscriptionCategory, string> = {
  hosting: 'hosting',
  db: 'base de datos',
  ia: 'IA',
  ofimatica: 'ofimática',
  dominio: 'dominio',
  pagos: 'pagos',
  marketing: 'marketing',
  otros: 'otros',
};

const BILLING_CYCLE_LABELS: Record<SubscriptionBillingCycle, string> = {
  monthly: 'mensual',
  annual: 'anual',
};

const SUBSCRIPTION_STATUS_LABELS: Record<SubscriptionStatus, string> = {
  active: 'activa',
  paused: 'pausada',
  cancelled: 'cancelada',
};

function subscriptionCategoryLabel(c: string): string {
  return (SUBSCRIPTION_CATEGORY_LABELS as Record<string, string>)[c] ?? c;
}

// Show the most concrete charge info available: a fixed next date wins;
// otherwise fall back to the recurring billing day.
function chargeLabel(s: Subscription): string {
  if (s.nextChargeDate) return fmtDate(s.nextChargeDate);
  if (s.billingDay != null) return `día ${s.billingDay} de cada mes`;
  return '—';
}

function fmtMoneyAmount(amount: number, currency: string): string {
  return `${amount.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}`;
}

function fmtByCurrency(map: Record<string, number>): string {
  const parts = Object.entries(map).map(([cur, v]) => fmtMoneyAmount(v, cur));
  return parts.length > 0 ? parts.join(' · ') : '—';
}

const CATEGORY_LABELS: Record<CashMovementCategory, string> = {
  aportacion_socio: 'aportación socio',
  cobro_sin_factura: 'cobro sin factura',
  prestamo: 'préstamo',
  gasto_personal_pagado: 'gasto personal pagado',
  reembolso: 'reembolso',
  otros: 'otros',
};

function categoryLabel(c: string): string {
  return (CATEGORY_LABELS as Record<string, string>)[c] ?? c;
}

const BUCKET_ORDER: AgingBucket[] = ['al-dia', '1-30', '31-60', '61-90', '90+'];
const BUCKET_LABELS: Record<AgingBucket, string> = {
  'al-dia': 'Al día',
  '1-30': '1-30 días',
  '31-60': '31-60',
  '61-90': '61-90',
  '90+': '90+',
};
const BUCKET_COLORS: Record<AgingBucket, string> = {
  'al-dia': 'var(--accent)',
  '1-30': '#e89a3e',
  '31-60': '#c8693e',
  '61-90': '#a8351a',
  '90+': '#6e1c12',
};

function bucketClass(b: AgingBucket): string {
  return b === 'al-dia' ? 'at-dia' : `b${b.replace('+', '')}`;
}

function chipClass(b: AgingBucket): string {
  return b === 'al-dia' ? 'al-dia' : `b${b.replace('+', '')}`;
}

export function Tesoreria({ scope, onToast, onError }: Props) {
  const [kpis, setKpis] = useState<TreasuryKpis | null>(null);
  const [aging, setAging] = useState<AgingResponse | null>(null);
  const [forecast, setForecast] = useState<CashForecastBucket[]>([]);
  const [dso, setDso] = useState<DsoResponse | null>(null);
  const [debtors, setDebtors] = useState<TopDebtor[]>([]);
  const [movements, setMovements] = useState<CashMovement[]>([]);
  const [movementTotals, setMovementTotals] = useState<{ income: number; expense: number; delta: number }>({
    income: 0,
    expense: 0,
    delta: 0,
  });
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState<CashMovement | null>(null);
  const [deleting, setDeleting] = useState<CashMovement | null>(null);
  const [subs, setSubs] = useState<Subscription[]>([]);
  const [subsSummary, setSubsSummary] = useState<SubscriptionsSummary>({
    activeCount: 0,
    monthlyByCurrency: {},
    annualByCurrency: {},
  });
  const [showSubCreate, setShowSubCreate] = useState(false);
  const [editingSub, setEditingSub] = useState<Subscription | null>(null);
  const [deletingSub, setDeletingSub] = useState<Subscription | null>(null);

  const loadAll = useCallback(async () => {
    const q = scope !== 'all' ? { scope } : undefined;
    try {
      const [k, a, f, d, t, m, s] = await Promise.all([
        api<TreasuryKpis>('/admin/treasury/kpis', { query: q }),
        api<AgingResponse>('/admin/treasury/aging', { query: q }),
        api<{ buckets: CashForecastBucket[] }>('/admin/treasury/forecast', { query: q }),
        api<DsoResponse>('/admin/treasury/dso', { query: q }),
        api<{ items: TopDebtor[] }>('/admin/treasury/top-debtors', { query: q }),
        api<CashMovementsResponse>('/admin/treasury/cash-movements'),
        api<SubscriptionsResponse>('/admin/treasury/subscriptions'),
      ]);
      setKpis(k);
      setAging(a);
      setForecast(f.buckets);
      setDso(d);
      setDebtors(t.items);
      setMovements(m.items);
      setMovementTotals(m.totals);
      setSubs(s.items);
      setSubsSummary(s.summary);
    } catch (e) {
      onError(e);
    }
  }, [scope, onError]);

  useEffect(() => {
    void loadAll();
  }, [loadAll]);

  async function handleDelete(id: string) {
    try {
      await api(`/admin/treasury/cash-movements/${id}`, { method: 'DELETE' });
      onToast('Movimiento borrado', 'ok');
      setDeleting(null);
      void loadAll();
    } catch (e) {
      onError(e);
    }
  }

  async function handleDeleteSub(id: string) {
    try {
      await api(`/admin/treasury/subscriptions/${id}`, { method: 'DELETE' });
      onToast('Suscripción borrada', 'ok');
      setDeletingSub(null);
      void loadAll();
    } catch (e) {
      onError(e);
    }
  }

  return (
    <div className="body">
      <div className="pageh">
        <h1>
          Tesorería <span className="v2-pill">cash 360</span>
        </h1>
        <div className="sub">
          scope = <span className="v">{scope === 'all' ? 'todos' : scope}</span>
        </div>
      </div>

      {kpis && (
        <div className="kpis">
          <div className="kpi k4">
            <div className="lbl">Saldo banco</div>
            <div className="v">{fmt(kpis.bankBalance)}</div>
            <div className="s">
              {kpis.collectedTotal !== 0 || kpis.manualDelta !== 0
                ? [
                    `inicial ${fmt(kpis.bankBalanceInitial)}`,
                    kpis.collectedTotal !== 0 ? `cobros +${fmt(kpis.collectedTotal)}` : null,
                    kpis.manualDelta !== 0
                      ? `manual ${kpis.manualDelta >= 0 ? '+' : ''}${fmt(kpis.manualDelta)}`
                      : null,
                  ]
                    .filter(Boolean)
                    .join(' · ')
                : 'inicial configurable desde Admin → Tesorería'}
            </div>
          </div>
          <div className="kpi k2">
            <div className="lbl">A cobrar 30 d.</div>
            <div className="v">{fmt(kpis.inflow30)}</div>
            <div className="s">
              {kpis.inflowInvoicesCount} factura{kpis.inflowInvoicesCount === 1 ? '' : 's'}
            </div>
          </div>
          <div className="kpi k3">
            <div className="lbl">A pagar 30 d.</div>
            <div className="v">{fmt(kpis.outflow30)}</div>
            <div className="s">gasto recurrente estructura</div>
          </div>
          <div className="kpi k1">
            <div className="lbl">Posición neta 30 d.</div>
            <div
              className="v"
              style={{ color: kpis.net30 >= 0 ? 'var(--accent-text)' : 'var(--red-text)' }}
            >
              {fmt(kpis.net30)}
            </div>
            <div className="s">
              {kpis.runwayMonths != null ? `runway ~${kpis.runwayMonths} meses` : 'runway: define gasto mensual'}
            </div>
          </div>
        </div>
      )}

      <div style={{ height: 12 }} />

      {aging && (
        <section className="card">
          <div className="head">
            <h3>Antigüedad de cobros · AR Aging</h3>
            <span style={{ color: 'var(--muted)', fontFamily: 'Geist Mono, monospace', fontSize: 11 }}>
              {aging.totalCount} facturas no cobradas · {fmt(aging.total)}
            </span>
            <div className="meta">netSuite · a/r aging</div>
          </div>
          <div className="pad">
            {aging.total > 0 ? (
              <>
                <div className="aging-bar">
                  {BUCKET_ORDER.map((k) => {
                    const v = aging.buckets[k] ?? 0;
                    if (v <= 0) return null;
                    const pct = (v / aging.total) * 100;
                    return (
                      <div
                        key={k}
                        className={`seg ${bucketClass(k)}`}
                        style={{ width: `${pct}%` }}
                        title={`${BUCKET_LABELS[k]} · ${fmt(v)}`}
                      >
                        {pct > 6 ? `${Math.round(pct)}%` : ''}
                      </div>
                    );
                  })}
                </div>
                <div className="aging-legend">
                  {BUCKET_ORDER.map((k) => (
                    <div key={k} className="item">
                      <div className="lab">
                        <span className="sw" style={{ background: BUCKET_COLORS[k] }} />
                        {BUCKET_LABELS[k]}
                      </div>
                      <div className="v">{fmt(aging.buckets[k] ?? 0)}</div>
                      <div className="c">
                        {aging.counts[k] ?? 0}{' '}
                        {(aging.counts[k] ?? 0) === 1 ? 'factura' : 'facturas'}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="empty">No hay facturas no cobradas en el scope</div>
            )}
          </div>
        </section>
      )}

      <div style={{ height: 12 }} />

      <div className="row2">
        <section className="card">
          <div className="head">
            <h3>Previsión de tesorería</h3>
            <div className="meta">próximos 90 días</div>
          </div>
          <div className="pad">
            <div className="cash-table">
              {forecast.map((b) => (
                <div className="cash-col" key={b.range}>
                  <div className="rng">{b.range}</div>
                  <div className="line in">
                    <span className="lab">Entradas</span>
                    <span className="v">+{fmt(b.inflow)}</span>
                  </div>
                  <div className="line out">
                    <span className="lab">Salidas</span>
                    <span className="v">−{fmt(b.outflow)}</span>
                  </div>
                  <div className="net">
                    <span className="lab">Neto</span>
                    <span className={`v ${b.net >= 0 ? 'pos' : 'neg'}`}>
                      {b.net >= 0 ? '+' : ''}
                      {fmt(b.net)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
            <div
              style={{
                marginTop: 14,
                padding: '10px 12px',
                border: '1px dashed var(--line)',
                borderRadius: 6,
                color: 'var(--muted)',
                fontFamily: 'Geist Mono, monospace',
                fontSize: 10.5,
                lineHeight: 1.6,
              }}
            >
              entradas = facturas pendientes por fecha de vencimiento · salidas = gasto mensual configurable
            </div>
          </div>
        </section>

        {dso && (
          <section className="card">
            <div className="head">
              <h3>DSO · Días medios de cobro</h3>
              <div className="meta">days sales outstanding</div>
            </div>
            <div className="pad" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
                <span
                  style={{
                    fontSize: 48,
                    fontWeight: 700,
                    letterSpacing: '-0.025em',
                    fontFeatureSettings: '"tnum"',
                  }}
                >
                  {dso.value}
                </span>
                <span style={{ color: 'var(--muted)', fontFamily: 'Geist Mono, monospace', fontSize: 13 }}>
                  días
                </span>
              </div>
              <div
                style={{
                  borderTop: '1px solid var(--line)',
                  paddingTop: 12,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 6,
                  fontFamily: 'Geist Mono, monospace',
                  fontSize: 11,
                  color: 'var(--muted)',
                }}
              >
                <Row k="objetivo" v={`≤ ${dso.target} días`} />
                <Row k="industria" v={`${dso.industry} días`} />
                <Row k="actual" v={`${dso.value} días`} accent />
              </div>
              {dso.recommendation && (
                <div
                  style={{
                    padding: '10px 12px',
                    background: 'var(--accent-bg)',
                    borderRadius: 5,
                    fontSize: 11.5,
                    color: 'var(--ink2)',
                    lineHeight: 1.55,
                  }}
                >
                  <b
                    style={{
                      color: 'var(--accent-text)',
                      fontFamily: 'Geist Mono, monospace',
                      fontSize: 10,
                      textTransform: 'uppercase',
                      letterSpacing: '0.08em',
                    }}
                  >
                    recomendación ·{' '}
                  </b>
                  {dso.recommendation}
                </div>
              )}
            </div>
          </section>
        )}
      </div>

      <div style={{ height: 12 }} />

      <section className="card">
        <div className="head">
          <h3>Top clientes con saldo vivo</h3>
          <div className="meta">ordenado por importe pendiente</div>
        </div>
        <table className="t">
          <thead>
            <tr>
              <th>Cliente</th>
              <th className="num">Facturas</th>
              <th className="num">Saldo vivo</th>
              <th>Antigüedad</th>
              <th>Crédito</th>
              <th>Predicción IA</th>
            </tr>
          </thead>
          <tbody>
            {debtors.map((d) => (
              <tr key={d.clientId}>
                <td data-label="Cliente">
                  <b>{d.clientName}</b>
                </td>
                <td className="num" data-label="Facturas">{d.invoices}</td>
                <td className="num" data-label="Saldo vivo">
                  <b>{fmt(d.balance)}</b>
                </td>
                <td data-label="Antigüedad">
                  <span className={`aging-chip ${chipClass(d.bucket)}`}>
                    {d.bucket === 'al-dia'
                      ? 'al día'
                      : d.bucket === '90+'
                      ? '> 90 d.'
                      : `${d.bucket} d.`}
                  </span>
                </td>
                <td data-label="Crédito">
                  {d.creditLimit > 0 ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 110 }}>
                      <div className="bar">
                        <i
                          style={{
                            width: `${Math.min(d.creditUsagePct, 100)}%`,
                            background:
                              d.creditUsagePct > 80 ? 'var(--red-text)' : 'var(--accent)',
                          }}
                        />
                      </div>
                      <span className="mono" style={{ fontSize: 10, color: 'var(--muted)' }}>
                        {Math.round(d.creditUsagePct)}%
                      </span>
                    </div>
                  ) : (
                    <span style={{ color: 'var(--muted)', fontSize: 11 }}>—</span>
                  )}
                </td>
                <td className="mono" style={{ color: 'var(--muted)', fontSize: 11 }} data-label="Predicción IA">
                  {d.paymentPrediction ? (
                    <span>
                      ~{d.paymentPrediction.days} d.{' '}
                      <span
                        style={{
                          color:
                            d.paymentPrediction.confidence === 'alta'
                              ? 'var(--accent-text)'
                              : 'var(--muted)',
                        }}
                      >
                        · conf. {d.paymentPrediction.confidence}
                      </span>
                    </span>
                  ) : (
                    '—'
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {debtors.length === 0 && <div className="empty">sin deudores</div>}
      </section>

      <div style={{ height: 12 }} />

      <section className="card">
        <div className="head">
          <h3>Movimientos manuales de caja</h3>
          <span
            style={{
              color: 'var(--muted)',
              fontFamily: 'Geist Mono, monospace',
              fontSize: 11,
            }}
          >
            {movements.length} movimiento{movements.length === 1 ? '' : 's'} · ingresos{' '}
            <b style={{ color: 'var(--accent-text)' }}>+{fmt(movementTotals.income)}</b> · gastos{' '}
            <b style={{ color: 'var(--red-text)' }}>−{fmt(movementTotals.expense)}</b> · neto{' '}
            <b style={{ color: movementTotals.delta >= 0 ? 'var(--accent-text)' : 'var(--red-text)' }}>
              {movementTotals.delta >= 0 ? '+' : ''}
              {fmt(movementTotals.delta)}
            </b>
          </span>
          <div className="meta" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <span>caja en efectivo · suma al saldo banco</span>
            <button className="btn primary" onClick={() => setShowCreate(true)}>
              <span className="plus">+</span> Registrar movimiento
            </button>
          </div>
        </div>
        <table className="t">
          <thead>
            <tr>
              <th>Fecha</th>
              <th>Tipo</th>
              <th>Categoría</th>
              <th>Descripción</th>
              <th>Por</th>
              <th className="num">Importe</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {movements.map((m) => (
              <tr key={m.id}>
                <td className="mono" data-label="Fecha">{fmtDate(m.date)}</td>
                <td data-label="Tipo">
                  <span
                    className="aging-chip"
                    style={{
                      background: m.type === 'income' ? 'var(--accent-bg)' : 'rgba(168, 53, 26, 0.12)',
                      color: m.type === 'income' ? 'var(--accent-text)' : 'var(--red-text)',
                    }}
                  >
                    {m.type === 'income' ? 'ingreso' : 'gasto'}
                  </span>
                </td>
                <td data-label="Categoría">{categoryLabel(m.category)}</td>
                <td style={{ color: m.description ? 'var(--ink)' : 'var(--muted)' }} data-label="Descripción">
                  {m.description ?? '—'}
                </td>
                <td className="mono" style={{ color: 'var(--muted)', fontSize: 11 }} data-label="Por">
                  {m.createdByCode ?? '—'}
                </td>
                <td
                  className="num"
                  data-label="Importe"
                  style={{
                    color: m.type === 'income' ? 'var(--accent-text)' : 'var(--red-text)',
                    fontWeight: 600,
                  }}
                >
                  {m.type === 'income' ? '+' : '−'}
                  {fmt(m.amount)}
                </td>
                <td data-label="">
                  <div className="row-actions">
                    <button
                      className="icon-btn"
                      title="Editar movimiento"
                      aria-label="Editar movimiento"
                      onClick={() => setEditing(m)}
                    >
                      <EditIcon />
                    </button>
                    <button
                      className="icon-btn danger"
                      title="Borrar movimiento"
                      aria-label="Borrar movimiento"
                      onClick={() => setDeleting(m)}
                    >
                      <TrashIcon />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {movements.length === 0 && (
          <div className="empty">
            Sin movimientos. Registra aportaciones, cobros sin factura o gastos pagados a título personal.
          </div>
        )}
      </section>

      <div style={{ height: 12 }} />

      <section className="card">
        <div className="head">
          <h3>Suscripciones</h3>
          <span
            style={{
              color: 'var(--muted)',
              fontFamily: 'Geist Mono, monospace',
              fontSize: 11,
            }}
          >
            {subsSummary.activeCount} activa{subsSummary.activeCount === 1 ? '' : 's'} · mensual{' '}
            <b style={{ color: 'var(--red-text)' }}>{fmtByCurrency(subsSummary.monthlyByCurrency)}</b> · anual{' '}
            <b style={{ color: 'var(--red-text)' }}>{fmtByCurrency(subsSummary.annualByCurrency)}</b>
          </span>
          <div className="meta" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <span>gasto recurrente en herramientas</span>
            <button className="btn primary" onClick={() => setShowSubCreate(true)}>
              <span className="plus">+</span> Nueva suscripción
            </button>
          </div>
        </div>
        <table className="t">
          <thead>
            <tr>
              <th>Servicio</th>
              <th>Categoría</th>
              <th>Ciclo</th>
              <th className="num">Precio</th>
              <th>Próximo cobro</th>
              <th>Dashboard</th>
              <th>Estado</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {subs.map((s) => (
              <tr key={s.id} style={{ opacity: s.status === 'active' ? 1 : 0.55 }}>
                <td data-label="Servicio">
                  <b>{s.name}</b>
                  {s.vendor && (
                    <span style={{ color: 'var(--muted)', fontSize: 11 }}> · {s.vendor}</span>
                  )}
                </td>
                <td data-label="Categoría">{subscriptionCategoryLabel(s.category)}</td>
                <td data-label="Ciclo">{BILLING_CYCLE_LABELS[s.billingCycle]}</td>
                <td className="num" data-label="Precio" style={{ fontWeight: 600 }}>
                  {fmtMoneyAmount(s.amount, s.currency)}
                </td>
                <td className="mono" data-label="Próximo cobro" style={{ fontSize: 11 }}>
                  {chargeLabel(s)}
                </td>
                <td data-label="Dashboard">
                  {s.dashboardUrl ? (
                    <a
                      href={s.dashboardUrl}
                      target="_blank"
                      rel="noreferrer"
                      style={{ color: 'var(--accent-text)' }}
                    >
                      abrir ↗
                    </a>
                  ) : (
                    <span style={{ color: 'var(--muted)' }}>—</span>
                  )}
                </td>
                <td data-label="Estado">
                  <span
                    className="aging-chip"
                    style={{
                      background: s.status === 'active' ? 'var(--accent-bg)' : 'rgba(120, 120, 120, 0.14)',
                      color: s.status === 'active' ? 'var(--accent-text)' : 'var(--muted)',
                    }}
                  >
                    {SUBSCRIPTION_STATUS_LABELS[s.status]}
                  </span>
                </td>
                <td data-label="">
                  <div className="row-actions">
                    <button
                      className="icon-btn"
                      title="Editar suscripción"
                      aria-label="Editar suscripción"
                      onClick={() => setEditingSub(s)}
                    >
                      <EditIcon />
                    </button>
                    <button
                      className="icon-btn danger"
                      title="Borrar suscripción"
                      aria-label="Borrar suscripción"
                      onClick={() => setDeletingSub(s)}
                    >
                      <TrashIcon />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {subs.length === 0 && (
          <div className="empty">
            Sin suscripciones. Registra herramientas como Google Workspace, Supabase o Railway para controlar
            lo que pagas.
          </div>
        )}
      </section>

      {showCreate && (
        <CashMovementModal
          onClose={() => setShowCreate(false)}
          onSaved={(action) => {
            setShowCreate(false);
            onToast(action === 'created' ? 'Movimiento registrado' : 'Movimiento actualizado', 'ok');
            void loadAll();
          }}
          onError={onError}
        />
      )}
      {editing && (
        <CashMovementModal
          initial={editing}
          onClose={() => setEditing(null)}
          onSaved={(action) => {
            setEditing(null);
            onToast(action === 'created' ? 'Movimiento registrado' : 'Movimiento actualizado', 'ok');
            void loadAll();
          }}
          onError={onError}
        />
      )}
      {deleting && (
        <ConfirmDialog
          tone="danger"
          title="Borrar movimiento"
          message={`Se borrará el ${deleting.type === 'income' ? 'ingreso' : 'gasto'} de ${fmt(deleting.amount)} del ${fmtDate(deleting.date)}. Afectará al saldo banco.`}
          confirmLabel="Borrar"
          onCancel={() => setDeleting(null)}
          onConfirm={() => handleDelete(deleting.id)}
        />
      )}
      {showSubCreate && (
        <SubscriptionModal
          onClose={() => setShowSubCreate(false)}
          onSaved={(action) => {
            setShowSubCreate(false);
            onToast(action === 'created' ? 'Suscripción creada' : 'Suscripción actualizada', 'ok');
            void loadAll();
          }}
          onError={onError}
        />
      )}
      {editingSub && (
        <SubscriptionModal
          initial={editingSub}
          onClose={() => setEditingSub(null)}
          onSaved={(action) => {
            setEditingSub(null);
            onToast(action === 'created' ? 'Suscripción creada' : 'Suscripción actualizada', 'ok');
            void loadAll();
          }}
          onError={onError}
        />
      )}
      {deletingSub && (
        <ConfirmDialog
          tone="danger"
          title="Borrar suscripción"
          message={`Se borrará la suscripción "${deletingSub.name}". Esto no afecta al saldo banco, solo al registro de control.`}
          confirmLabel="Borrar"
          onCancel={() => setDeletingSub(null)}
          onConfirm={() => handleDeleteSub(deletingSub.id)}
        />
      )}
    </div>
  );
}

function CashMovementModal(props: {
  initial?: CashMovement;
  onClose: () => void;
  onSaved: (action: 'created' | 'updated') => void;
  onError: (e: unknown) => void;
}) {
  const isEdit = !!props.initial;
  const [type, setType] = useState<CashMovementType>(props.initial?.type ?? 'income');
  const [amount, setAmount] = useState<number>(props.initial?.amount ?? 0);
  const [date, setDate] = useState(props.initial?.date ?? todayIso());
  const [category, setCategory] = useState<CashMovementCategory>(
    (props.initial?.category as CashMovementCategory) ?? 'aportacion_socio',
  );
  const [description, setDescription] = useState(props.initial?.description ?? '');
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (amount <= 0) {
      props.onError(new Error('El importe debe ser mayor que 0'));
      return;
    }
    setBusy(true);
    try {
      const body = {
        type,
        amount,
        date,
        category,
        description: description.trim() || null,
      };
      if (isEdit && props.initial) {
        await api(`/admin/treasury/cash-movements/${props.initial.id}`, {
          method: 'PATCH',
          body,
        });
        props.onSaved('updated');
      } else {
        await api('/admin/treasury/cash-movements', { method: 'POST', body });
        props.onSaved('created');
      }
    } catch (e) {
      props.onError(e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={props.onClose}>
      <form className="modal" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <h3>{isEdit ? 'Editar movimiento' : 'Registrar movimiento'}</h3>
        <div className="field">
          <label>Tipo</label>
          <select value={type} onChange={(e) => setType(e.target.value as CashMovementType)}>
            <option value="income">Ingreso (suma al saldo)</option>
            <option value="expense">Gasto (resta al saldo)</option>
          </select>
        </div>
        <div className="field">
          <label>Importe (€)</label>
          <input
            type="number"
            step="0.01"
            min={0}
            value={amount}
            onChange={(e) => setAmount(Number.parseFloat(e.target.value) || 0)}
            required
          />
        </div>
        <div className="field">
          <label>Fecha</label>
          <DateInput value={date} onChange={setDate} required />
        </div>
        <div className="field">
          <label>Categoría</label>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value as CashMovementCategory)}
          >
            {CASH_MOVEMENT_CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {(CATEGORY_LABELS as Record<string, string>)[c] ?? c}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label>Descripción</label>
          <input
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={500}
            placeholder="opcional"
          />
        </div>
        <div
          style={{
            fontFamily: 'Geist Mono, monospace',
            fontSize: 10.5,
            color: 'var(--muted)',
            lineHeight: 1.5,
          }}
        >
          Movimiento {type === 'income' ? 'sumará' : 'restará'} <b>{fmt(amount)}</b> al saldo banco a partir de {fmtDate(date)}.
        </div>
        <div className="actions">
          <button type="button" className="btn ghost" onClick={props.onClose}>
            Cancelar
          </button>
          <button type="submit" className="btn primary" disabled={busy || amount <= 0}>
            {isEdit ? 'Guardar' : 'Registrar'}
          </button>
        </div>
      </form>
    </div>
  );
}

function SubscriptionModal(props: {
  initial?: Subscription;
  onClose: () => void;
  onSaved: (action: 'created' | 'updated') => void;
  onError: (e: unknown) => void;
}) {
  const isEdit = !!props.initial;
  const [name, setName] = useState(props.initial?.name ?? '');
  const [vendor, setVendor] = useState(props.initial?.vendor ?? '');
  const [category, setCategory] = useState<SubscriptionCategory>(
    (props.initial?.category as SubscriptionCategory) ?? 'otros',
  );
  const [billingCycle, setBillingCycle] = useState<SubscriptionBillingCycle>(
    props.initial?.billingCycle ?? 'monthly',
  );
  const [amount, setAmount] = useState<number>(props.initial?.amount ?? 0);
  const [currency, setCurrency] = useState(props.initial?.currency ?? 'EUR');
  const [dashboardUrl, setDashboardUrl] = useState(props.initial?.dashboardUrl ?? '');
  const [billingDay, setBillingDay] = useState<string>(
    props.initial?.billingDay != null ? String(props.initial.billingDay) : '',
  );
  const [nextChargeDate, setNextChargeDate] = useState(props.initial?.nextChargeDate ?? '');
  const [status, setStatus] = useState<SubscriptionStatus>(props.initial?.status ?? 'active');
  const [paymentMethod, setPaymentMethod] = useState(props.initial?.paymentMethod ?? '');
  const [notes, setNotes] = useState(props.initial?.notes ?? '');
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (name.trim().length === 0) {
      props.onError(new Error('El nombre es obligatorio'));
      return;
    }
    if (amount < 0) {
      props.onError(new Error('El precio no puede ser negativo'));
      return;
    }
    const dayNum = billingDay.trim() === '' ? null : Number.parseInt(billingDay, 10);
    if (dayNum !== null && (Number.isNaN(dayNum) || dayNum < 1 || dayNum > 31)) {
      props.onError(new Error('El día de cobro debe estar entre 1 y 31'));
      return;
    }
    setBusy(true);
    try {
      const body = {
        name: name.trim(),
        vendor: vendor.trim() || null,
        category,
        billingCycle,
        amount,
        currency: currency.trim().toUpperCase() || 'EUR',
        dashboardUrl: dashboardUrl.trim() || null,
        billingDay: dayNum,
        nextChargeDate: nextChargeDate || null,
        status,
        paymentMethod: paymentMethod.trim() || null,
        notes: notes.trim() || null,
      };
      if (isEdit && props.initial) {
        await api(`/admin/treasury/subscriptions/${props.initial.id}`, { method: 'PATCH', body });
        props.onSaved('updated');
      } else {
        await api('/admin/treasury/subscriptions', { method: 'POST', body });
        props.onSaved('created');
      }
    } catch (e) {
      props.onError(e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={props.onClose}>
      <form className="modal" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <h3>{isEdit ? 'Editar suscripción' : 'Nueva suscripción'}</h3>
        <div className="field">
          <label>Servicio</label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={200}
            placeholder="Google Workspace, Supabase, Railway…"
            required
          />
        </div>
        <div className="field">
          <label>Proveedor / origen</label>
          <input
            value={vendor}
            onChange={(e) => setVendor(e.target.value)}
            maxLength={200}
            placeholder="opcional · p. ej. Google LLC"
          />
        </div>
        <div className="field">
          <label>Categoría</label>
          <select value={category} onChange={(e) => setCategory(e.target.value as SubscriptionCategory)}>
            {SUBSCRIPTION_CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {subscriptionCategoryLabel(c)}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label>Ciclo de facturación</label>
          <select
            value={billingCycle}
            onChange={(e) => setBillingCycle(e.target.value as SubscriptionBillingCycle)}
          >
            <option value="monthly">Mensual</option>
            <option value="annual">Anual</option>
          </select>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <div className="field" style={{ flex: 2 }}>
            <label>Precio por ciclo</label>
            <input
              type="number"
              step="0.01"
              min={0}
              value={amount}
              onChange={(e) => setAmount(Number.parseFloat(e.target.value) || 0)}
              required
            />
          </div>
          <div className="field" style={{ flex: 1 }}>
            <label>Divisa</label>
            <input
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              maxLength={8}
              placeholder="EUR"
            />
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <div className="field" style={{ flex: 1 }}>
            <label>Día de cobro</label>
            <input
              type="number"
              min={1}
              max={31}
              value={billingDay}
              onChange={(e) => setBillingDay(e.target.value)}
              placeholder="1-31 · opcional"
            />
          </div>
          <div className="field" style={{ flex: 1 }}>
            <label>Próxima fecha de cobro</label>
            <DateInput value={nextChargeDate} onChange={setNextChargeDate} />
          </div>
        </div>
        <div className="field">
          <label>Dashboard (URL)</label>
          <input
            type="url"
            value={dashboardUrl}
            onChange={(e) => setDashboardUrl(e.target.value)}
            maxLength={500}
            placeholder="https://… · opcional"
          />
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <div className="field" style={{ flex: 1 }}>
            <label>Estado</label>
            <select value={status} onChange={(e) => setStatus(e.target.value as SubscriptionStatus)}>
              <option value="active">Activa</option>
              <option value="paused">Pausada</option>
              <option value="cancelled">Cancelada</option>
            </select>
          </div>
          <div className="field" style={{ flex: 1 }}>
            <label>Método de pago</label>
            <input
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value)}
              maxLength={200}
              placeholder="opcional · tarjeta/cuenta"
            />
          </div>
        </div>
        <div className="field">
          <label>Notas</label>
          <input
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            maxLength={1000}
            placeholder="opcional"
          />
        </div>
        <div
          style={{
            fontFamily: 'Geist Mono, monospace',
            fontSize: 10.5,
            color: 'var(--muted)',
            lineHeight: 1.5,
          }}
        >
          Coste mensual equivalente:{' '}
          <b>
            {fmtMoneyAmount(
              billingCycle === 'annual' ? Math.round((amount / 12) * 100) / 100 : amount,
              currency.trim().toUpperCase() || 'EUR',
            )}
          </b>
          . Solo es un registro de control; no afecta al saldo banco.
        </div>
        <div className="actions">
          <button type="button" className="btn ghost" onClick={props.onClose}>
            Cancelar
          </button>
          <button type="submit" className="btn primary" disabled={busy || name.trim().length === 0}>
            {isEdit ? 'Guardar' : 'Crear'}
          </button>
        </div>
      </form>
    </div>
  );
}

function Row({ k, v, accent }: { k: string; v: string; accent?: boolean }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
      <span>{k}</span>
      <b style={{ color: accent ? 'var(--accent-text)' : 'var(--ink)' }}>{v}</b>
    </div>
  );
}
